import * as React from 'react'
import type { VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'
import { spinnerVariants } from './spinner-variants'

export interface SpinnerProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof spinnerVariants> {
  label?: string
}

const DotsSpinner = React.forwardRef<HTMLDivElement, SpinnerProps>(
  ({ className, size, label, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex space-x-1', className)}
      role="status"
      aria-label={label || 'Carregando'}
      {...props}
    >
      {[0, 150, 300].map((delay) => (
        <div
          key={delay}
          className={cn(
            'animate-bounce rounded-full bg-current',
            size === 'sm'
              ? 'h-1 w-1'
              : size === 'lg'
                ? 'h-2 w-2'
                : size === 'xl'
                  ? 'h-3 w-3'
                  : 'h-1.5 w-1.5'
          )}
          style={{ animationDelay: `${delay}ms` }}
        />
      ))}
    </div>
  )
)
DotsSpinner.displayName = 'DotsSpinner'

const BounceSpinner = React.forwardRef<HTMLDivElement, SpinnerProps>(
  ({ className, size, label, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex items-center justify-center', className)}
      role="status"
      aria-label={label || 'Carregando'}
      {...props}
    >
      <div
        className={cn(
          'animate-bounce rounded-full bg-current',
          size === 'sm'
            ? 'h-2 w-2'
            : size === 'lg'
              ? 'h-4 w-4'
              : size === 'xl'
                ? 'h-6 w-6'
                : 'h-3 w-3'
        )}
      />
    </div>
  )
)
BounceSpinner.displayName = 'BounceSpinner'

export const Spinner = React.forwardRef<HTMLDivElement, SpinnerProps>(
  ({ className, variant = 'default', size, label, ...props }, ref) => {
    if (variant === 'dots') {
      return <DotsSpinner ref={ref} className={className} size={size} label={label} {...props} />
    }

    if (variant === 'bounce') {
      return <BounceSpinner ref={ref} className={className} size={size} label={label} {...props} />
    }

    return (
      <div
        ref={ref}
        className={cn(spinnerVariants({ variant, size, className }))}
        role="status"
        aria-label={label || 'Carregando'}
        {...props}
      >
        <span className="sr-only">{label || 'Carregando...'}</span>
      </div>
    )
  }
)
Spinner.displayName = 'Spinner'

export interface LoadingPageProps {
  message?: string
  variant?: VariantProps<typeof spinnerVariants>['variant']
  size?: VariantProps<typeof spinnerVariants>['size']
}

export const LoadingPage = React.forwardRef<HTMLDivElement, LoadingPageProps>(
  ({ message = 'Carregando...', variant = 'default', size = 'lg' }, ref) => (
    <div ref={ref} className="flex min-h-[400px] flex-col items-center justify-center space-y-4">
      <Spinner variant={variant} size={size} />
      <p className="animate-pulse text-sm text-muted-foreground">{message}</p>
    </div>
  )
)
LoadingPage.displayName = 'LoadingPage'

export interface LoadingInlineProps {
  message?: string
  variant?: VariantProps<typeof spinnerVariants>['variant']
  size?: VariantProps<typeof spinnerVariants>['size']
}

export const LoadingInline = React.forwardRef<HTMLDivElement, LoadingInlineProps>(
  ({ message, variant = 'default', size = 'sm' }, ref) => (
    <div ref={ref} className="flex items-center space-x-2">
      <Spinner variant={variant} size={size} />
      {message && <span className="text-sm text-muted-foreground">{message}</span>}
    </div>
  )
)
LoadingInline.displayName = 'LoadingInline'
